const IMG = {
    dashboard: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1600&q=80",
    tradingScreens: "https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?auto=format&fit=crop&w=1600&q=80",
    coins: "https://images.unsplash.com/photo-1621761191319-c6fb62004040?auto=format&fit=crop&w=1600&q=80",
    chartClose: "https://images.unsplash.com/photo-1535320903710-d993d3d77d29?auto=format&fit=crop&w=1600&q=80",
    laptop: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1600&q=80",
    darkChart: "https://images.unsplash.com/photo-1642104704074-907c0698cbd9?auto=format&fit=crop&w=1600&q=80",
};

export const BLOGS_B = [
    {
        slug: "backtesting-trading-bot-overfitting",
        title: "Backtesting Your Trading Bot: Avoiding the Overfitting Trap",
        excerpt:
            "A backtest that looks too good usually is. How professional quants separate genuine edge from curve-fit fiction before a single live dollar is risked.",
        category: "Trading Bots",
        date: "Mar 26, 2026",
        readTime: "7 min read",
        image: IMG.dashboard,
        content: [
            {
                p: [
                    "Every failed trading bot once had a beautiful backtest. The graveyard of algorithmic trading is filled with equity curves that rose at forty-five degrees right up until real money was attached. The cause is almost always the same: overfitting — tuning a strategy so precisely to historical data that it has memorised the past instead of learning anything transferable about the market.",
                ],
            },
            {
                h: "What overfitting looks like",
                p: [
                    "An overfit system typically has many parameters, each tuned to a precise value — a 23-period filter that works, while 21 and 25 fail badly. Genuine edges live on plateaus: if 18, 20 and 22 all work, the effect is probably real. If only the magic number works, you have found noise with a good press release.",
                ],
            },
            {
                h: "The professional validation stack",
                p: [
                    "Start by splitting your data: optimise on the in-sample period, then validate once on out-of-sample data you never touched during development. Follow with walk-forward analysis — rolling the optimisation window forward through time to prove the edge survives regime change. Stress the result with realistic costs: spreads at their widest, slippage on every entry, and the occasional gap through your stop.",
                    "Finally, run a Monte Carlo shuffle of the trade sequence. The order of wins and losses is luck; if random reordering produces ruin-level drawdowns a third of the time, your position sizing — not your entry logic — is the real risk.",
                ],
            },
            {
                h: "The rule that saves the most money",
                p: [
                    "Fewer parameters, more humility. A three-parameter strategy that survives everything above is worth more than a twelve-parameter masterpiece that only works in hindsight. At Pocketfolio, no bot ships without passing every layer of this stack — which is why most of our research never ships at all.",
                ],
            },
        ],
    },
    {
        slug: "sp500-us-dollar-correlation",
        title: "Stock Indices and Forex: How the S&P 500 Talks to the Dollar",
        excerpt:
            "Risk-on, risk-off, and the interest-rate channel — what currency traders can learn by watching the world's most important equity index.",
        category: "Stocks",
        date: "Mar 12, 2026",
        readTime: "6 min read",
        image: IMG.tradingScreens,
        content: [
            {
                p: [
                    "The S&P 500 and the US dollar are linked by one of the most useful relationships in macro trading — and one of the most misunderstood. Many traders assume a strong stock market means a strong dollar. History says the relationship is usually the opposite, and the reason why tells you a great deal about how global capital actually moves.",
                ],
            },
            {
                h: "The risk regime channel",
                p: [
                    "In stress, the dollar is the world's hiding place. When equities fall sharply, global investors repatriate capital into USD-denominated safety, and the dollar index rises — the 2008 and March 2020 episodes are the textbook cases. In calm, risk-on regimes, capital flows out of the dollar and into equities, carry trades and emerging markets. This is why DXY and the S&P 500 are inversely correlated in most regimes: they are opposite ends of the same risk seesaw.",
                ],
            },
            {
                h: "The interest-rate channel",
                p: [
                    "Rates complicate the picture. Rising US yields support the dollar while pressuring equity valuations, especially the long-duration growth stocks that dominate the Nasdaq 100. In a rate-shock regime, the dollar and equities can fall or rise together for weeks, and traders anchored to the 'normal' inverse correlation get run over. Correlations are regime-dependent; treat them as weather, not law.",
                ],
            },
            {
                h: "Using SPX as a forex filter",
                p: [
                    "Our desks use the index as a context filter rather than a trigger. A breakout in USD/JPY carries more conviction when equities are stable or rising — the classic carry-friendly environment. A commodity-currency long looks fragile when SPX is breaking weekly support, whatever the chart says. The index rarely tells you what to trade; it often tells you what not to.",
                ],
            },
        ],
    },
    {
        slug: "gold-silver-ratio-trading",
        title: "Silver vs Gold: Trading the Gold-Silver Ratio",
        excerpt:
            "One number has described the relationship between the two monetary metals for centuries. How traders use the ratio's extremes to time the white metal.",
        category: "Commodities",
        date: "Feb 26, 2026",
        readTime: "5 min read",
        image: IMG.coins,
        content: [
            {
                p: [
                    "The gold-silver ratio is simply the price of an ounce of gold divided by the price of an ounce of silver — the number of silver ounces needed to buy one of gold. It has been quoted since antiquity, and while it no longer enjoys official status, its extremes remain among the most watched mean-reversion signals in the commodity world.",
                ],
            },
            {
                h: "What the ratio tells you",
                p: [
                    "Over the modern era the ratio has spent much of its time between roughly 55 and 80. Readings above 80 have historically marked silver as cheap relative to gold — the March 2020 panic briefly pushed it beyond 120, an all-time extreme, before silver outperformed dramatically over the following year. Low readings, toward 40–50, have marked the opposite: silver euphoria, as in 2011.",
                    "The logic is structural. Gold is primarily a monetary asset; silver is half monetary, half industrial. In risk panics, money hides in gold first and the ratio spikes. In reflation and growth phases, silver's industrial demand kicks in and the ratio compresses.",
                ],
            },
            {
                h: "How traders express it",
                p: [
                    "The purist's trade is a pairs position: long XAG/USD against short XAU/USD when the ratio is historically stretched, betting on convergence rather than outright direction. Simpler expressions work too — overweighting silver exposure via SLV when the ratio exceeds 80, or simply using a high ratio as a quality filter that upgrades long-silver setups.",
                    "Respect the volatility difference. Silver behaves like gold with leverage: daily swings of 3% are routine, and position sizes should be cut accordingly. The ratio gives you the when. Sizing discipline still decides whether you are around to collect.",
                ],
            },
        ],
    },
    {
        slug: "evaluate-forex-signal-provider",
        title: "How to Evaluate a Forex Signal Provider Before You Subscribe",
        excerpt:
            "Verified track records, drawdown honesty and sample size — a due-diligence checklist that filters out 95% of the signal market before it costs you money.",
        category: "Signals",
        date: "Feb 12, 2026",
        readTime: "6 min read",
        image: IMG.chartClose,
        content: [
            {
                p: [
                    "The signal industry has a survivorship problem: for every desk publishing disciplined, verifiable calls, dozens of accounts sell screenshots and hope. The good news is that serious providers are easy to identify once you know what evidence to demand. This is the checklist our own desk would apply to a competitor.",
                ],
            },
            {
                h: "Demand verified performance",
                p: [
                    "Self-reported spreadsheets are marketing, not track records. Look for third-party verification — platforms like Myfxbook or FX Blue that connect directly to the trading account and cannot be edited retroactively. Require a meaningful sample: at least two hundred trades across different market conditions, ideally a year or more. Thirty winning trades in a trending month prove nothing except that trends exist.",
                ],
            },
            {
                h: "Read the drawdown, not the return",
                p: [
                    "Returns tell you what a provider made; maximum drawdown tells you what it cost to make it. A service returning 60% a year with a 50% drawdown is a coin flip with better branding. Compare return to pain: sustained performance at two to three times the maximum drawdown is the profile of a professional operation.",
                ],
            },
            {
                h: "Inspect the signals themselves",
                p: [
                    "Every signal should carry a stop-loss, defined targets and enough context to evaluate late entry. Ask how losing streaks are communicated — providers who go quiet during drawdowns will do the same with your money on the line. Finally, check the risk model: any provider sizing with martingale or grid recovery is not managing risk, it is postponing it.",
                    "A legitimate desk will welcome every question on this list. Evasion is the only answer you need.",
                ],
            },
        ],
    },
    {
        slug: "position-sizing-101",
        title: "Position Sizing 101: The Mathematics of Staying in the Game",
        excerpt:
            "Entries get the glory, but sizing decides survival. The fixed-fraction method, worked examples, and why the Kelly criterion should be handled with care.",
        category: "Education",
        date: "Jan 29, 2026",
        readTime: "7 min read",
        image: IMG.laptop,
        content: [
            {
                p: [
                    "Ask a room of traders what matters most and most will say entries. Ask the ones still trading after a decade and they will say sizing. A mediocre entry with professional sizing survives to trade tomorrow; a brilliant entry with reckless sizing eventually meets the one loss that ends the account. Position sizing is not a detail of your strategy — it is the strategy's immune system.",
                ],
            },
            {
                h: "The fixed-fraction method",
                p: [
                    "The professional standard is simple: risk a fixed percentage of current equity on every trade — most desks use 0.5–2%. The formula is: position size equals risk capital divided by stop distance. Worked example: a $10,000 account risking 1% has $100 of risk capital. On a gold trade with a 25-dollar stop, that is four ounces — because 4 × $25 equals the $100 you have agreed to lose. Nothing else about the trade changes the size.",
                    "Two properties make this powerful. Risk shrinks automatically during drawdowns, so losing streaks decay geometrically instead of collapsing linearly. And size grows with the account, compounding gains without any change in behaviour.",
                ],
            },
            {
                h: "What about Kelly?",
                p: [
                    "The Kelly criterion computes the mathematically optimal bet size from your win rate and payoff ratio — and it is genuinely optimal in theory. In practice it demands accurate knowledge of your own edge, which is exactly what traders overestimate. Full-Kelly sizing produces drawdowns most humans cannot psychologically survive. Professionals who use Kelly at all typically trade a quarter or half of it.",
                ],
            },
            {
                h: "Sizing rules worth engraving",
                p: [
                    "Never increase size to recover a loss. Recalculate size from current equity before every trade, not from the equity you remember. And cap your total portfolio heat — the sum of open risk across all positions — so that a single correlated shock cannot take out more than a predefined slice of the account. Survival first; the returns are a by-product.",
                ],
            },
        ],
    },
    {
        slug: "trading-psychology-automation",
        title: "Trading Psychology: Why Bots Remove Emotion — and When They Don't",
        excerpt:
            "Automation deletes fear and greed from execution, then hands them back to you at the portfolio level. How disciplined operators manage the human behind the machine.",
        category: "Education",
        date: "Jan 15, 2026",
        readTime: "6 min read",
        image: IMG.darkChart,
        content: [
            {
                p: [
                    "The standard pitch for algorithmic trading is psychological: the bot never panics, never chases, never revenge-trades. All true. But automation does not eliminate emotion from trading — it relocates it. The feelings move from the moment of execution to the moment you watch the equity curve, and the trader who interferes with a sound system at the wrong time can destroy more value than any manual mistake.",
                ],
            },
            {
                h: "The interference problem",
                p: [
                    "Research on discretionary intervention in systematic strategies tells a consistent story: humans override their systems at the worst moments — disabling them after drawdowns, at the exact point where the edge is statistically most likely to reassert, and re-enabling them after hot streaks, just in time for mean reversion. The bot's discipline is only as strong as the operator's decision to leave it alone.",
                ],
            },
            {
                h: "A trust protocol for operators",
                p: [
                    "Professional operators solve this with process, not willpower. Intervention rules are written in advance: the system may only be disabled by the kill switch, by a scheduled review, or by a predefined statistical breach — never by a feeling. Reviews happen on a calendar, weekly or monthly, not in response to P&L. Between reviews, the operator's job is monitoring infrastructure, not second-guessing trades.",
                    "Journalling helps too — but the journal entry that matters is not about the market. It is the honest record of every time you wanted to interfere and what happened because you did or didn't.",
                ],
            },
            {
                h: "Where discretion still belongs",
                p: [
                    "None of this makes humans obsolete. Judgement still owns the domains algorithms cannot see: unprecedented events, broken correlations, liquidity holes, and the decision to retire a strategy whose world has changed. The mature model is not human or machine. It is machine for execution, human for governance — and a clear, written border between the two.",
                ],
            },
        ],
    },
];
